import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useParams } from "react-router-dom";
import api from "src/services/api";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAuthenticate from "src/hooks/useAuthenticate";
import logo from "../../../../assets/images/branding/logo-torneiofacil.png";
import "./style.css";
import NavBar from "../../navbar";
import { keyframes, styled } from "@mui/system";


// Animação para piscar
const blink = keyframes`
  0%, 80%, 100% { opacity: 1; } /* acesa na maior parte */
  81%, 95% { opacity: 0; }      /* apagada por pouco tempo */
`;


// Texto piscante personalizado
const BlinkText = styled("h1")({
  fontSize: "2.5rem",
  fontWeight: "bold",
  color: "#ab0000", // cor do texto
  textAlign: "center",
  animation: `${blink} 1s infinite ease-in-out`, // pisca suave
});


const pageContainerStyle = {
  p: 4,
  width: "100%",
  maxWidth: 1200,
  // margin: "20px auto", // A margem será controlada pelo .full-screen-container
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 3,
  display: "flex",
  flexDirection: "column",
  // minHeight: "calc(100vh - 40px)", // A altura será controlada pelo .full-screen-container e escala
};

interface Fighter {
  gym: string;
  name: string;
}

interface Competitor {
  lutador: Fighter | "";
  posicao: number;
}

interface TournamentBracketProps {
  competitors: Competitor[][];
  category: string;
  tournamentName: string;
  tournamentLocation: string;
  tournamentDate: string;
  index: number;
  totalPages: number;
  fighterNameFilter: string;
  gymFilter: string;
  is32Bracket?: boolean;
  isPartTwo?: boolean;
  rules?: any;
}

const TournamentBracket = ({
  competitors,
  category,
  tournamentName,
  tournamentLocation,
  tournamentDate,
  index,
  totalPages,
  fighterNameFilter,
  gymFilter,
  is32Bracket = false,
  isPartTwo = false,
  rules
}: TournamentBracketProps) => {
  const getCompetitorData = (position: number) => {
    const highlightText = (text: string, filter: string) => {
      if (!filter) return text;
      const regex = new RegExp(`(${filter})`, "gi");
      return text.split(regex).map((part, i) =>
        regex.test(part) ? (
          <span key={i} style={{ backgroundColor: "yellow" }}>
            {part}
          </span>
        ) : (
          part
        )
      );
    };

    for (const match of competitors) {
      if (match[0] && match[0].posicao === position) {
        const fighter = match[0].lutador;
        if (typeof fighter !== "string") {
          return (
            <>
              <span className="figther-name">
                {highlightText(fighter.name, fighterNameFilter)}
              </span>
              <span className="figther-gym">
                {highlightText(fighter.gym || "Sem academia", gymFilter)}
              </span>
            </>
          );
        }
      }
      if (match[1] && match[1].posicao === position) {
        const fighter = match[1].lutador;
        if (typeof fighter !== "string") {
          return (
            <>
              <span className="figther-name">
                {highlightText(fighter.name, fighterNameFilter)}
              </span>
              <span className="figther-gym">
                {highlightText(fighter.gym || "Sem academia", gymFilter)}
              </span>
            </>
          );
        }
      }
    }
    return "";
  };

  const normalize = (text?: string) =>
    (text || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, "");

  const getDescriptionFromCategory = (categoryString: string) => {
    const raw = (categoryString || "").trim();
    const clean = raw.replace(/-parte\d+/gi, "").replace(/\s+/g, " ").trim();

    // Detecta "luta casada - 1" / "Luta Casada:2" etc.
    const manualMatch = clean.match(/luta\s*casada\s*[-:]?\s*(\d+)?/i);
    const isManual = /luta\s*casada/i.test(clean);
    const manualNumber = manualMatch?.[1] ? Number(manualMatch[1]) : null;

    // ✅ Se não vier no formato com "/", não tenta splitar
    if (!clean.includes("/")) {
      return {
        hasRules: false,
        isManual,
        manualNumber: isManual ? manualNumber : null,
        ageDescription: "",
        pesoDescription: "",
        fightTime: "",
      };
    }

    const parts = clean
      .toLowerCase()
      .split("/")
      .map((p) => p.trim())
      .filter(Boolean);

    // ✅ Precisa de 4 partes: categoria/gênero/faixa/peso
    if (parts.length < 4) {
      return {
        hasRules: false,
        isManual: false,
        manualNumber: null,
        ageDescription: "--",
        pesoDescription: "--",
        fightTime: "--",
      };
    }

    const category = normalize(parts[0]);
    const gender = normalize(parts[1]);
    const belt = normalize(parts[2]);
    const pesoDesc = normalize(parts[3]);

    const matchCategory = (rules || []).find((rule: any) => {
      const jsonGender = normalize(rule.genero);
      const jsonCategory = normalize(rule.categoria);
      const jsonBelts = (rule.faixa || []).map((f: any) => normalize(f.nome));
      const jsonPesoDesc = normalize(rule.peso?.descricao);

      return (
        jsonGender === gender &&
        jsonCategory === category &&
        jsonBelts.some((b: string) => b === belt) &&
        jsonPesoDesc === pesoDesc
      );
    });

    if (!matchCategory) {
      return {
        hasRules: false,
        isManual: false,
        manualNumber: null,
        ageDescription: "--",
        pesoDescription: "--",
        fightTime: "--",
      };
    }

    const beltFightTime = (matchCategory.faixa || []).find(
      (b: { nome: string }) => normalize(b.nome) === belt
    );

    return {
      hasRules: true,
      isManual: false,
      manualNumber: null,
      ageDescription: matchCategory.idade?.descricaoCompleta || "--",
      pesoDescription: matchCategory.peso?.max || "--",
      fightTime: beltFightTime?.tempodeLuta || "--",
    };
  };



  const formatBrazilianDate = (dateString: string) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const renderLeftCompetitors = () => {
    let indicesInThisSide;

    if (is32Bracket) {
      if (isPartTwo) {
        indicesInThisSide = [
          [2, 18],
          [10, 26],
          [6, 22],
          [14, 30],
        ];
      } else {
        indicesInThisSide = [
          [1, 17],
          [9, 25],
          [5, 21],
          [13, 29],
        ];
      }
    } else {
      // 16-person bracket logic
      indicesInThisSide = [
        [1, 9],
        [5, 13],
        [3, 11],
        [7, 15],
      ];
    }

    return indicesInThisSide.map((c: number[], i: number) => (
      <div className="match-container" key={i}>
        <div className="match-number-1">{c[0]}</div>
        <div className="match-number-2">{c[1]}</div>
        <div className="match-box-left">{getCompetitorData(c[0])}</div>
        <div className="match-box-left">{getCompetitorData(c[1])}</div>
        <div className="connector-line" />

        <div className="lines-container">
          <div className="line1TopLeft"></div>
          <div className="line1CenterLeft"></div>
          <div className="line1BottomLeft"></div>

          <div className="line2TopLeft"></div>
          {i % 2 === 0 && <div className="line2CenterLeft"></div>}

          {i == 0 && <div className="lineCenterLeft"></div>}
          {i == 0 && <div className="lineCenterRight"></div>}
          {i % 2 === 0 && <div className="lineTopRight"></div>}
          {i % 2 === 0 && <div className="lineTopLeft"></div>}

          <div className="line1TopRight"></div>
          <div className="line1CenterRight"></div>
          <div className="line1BottomRight"></div>

          <div className="line2TopRight"></div>
          {i % 2 === 0 && <div className="line2CenterRight"></div>}
          {i === 0 && <div className="lineCenterHorizontal"></div>}
          {i === 0 && <div className="lineCenterHorizontalTop"></div>}
        </div>
      </div>
    ));
  };

  const renderRightCompetitors = () => {
    let indicesInThisSide;

    if (is32Bracket) {
      if (isPartTwo) {
        indicesInThisSide = [
          [4, 20],
          [12, 28],
          [8, 24],
          [16, 32],
        ];
      } else {
        indicesInThisSide = [
          [3, 19],
          [11, 27],
          [7, 23],
          [15, 31],
        ];
      }
    } else {
      // 16-person bracket logic
      indicesInThisSide = [
        [2, 10],
        [6, 14],
        [4, 12],
        [8, 16],
      ];
    }

    return indicesInThisSide.map((c: number[], i: number) => (
      <div className="match-container" key={i}>
        <div className="match-number-right-1">{c[0]}</div>
        <div className="match-number-right-2">{c[1]}</div>
        <div className="match-box-right">{getCompetitorData(c[0])}</div>
        <div className="match-box-right">{getCompetitorData(c[1])}</div>
        <div className="connector-line" />
      </div>
    ));
  };


  const desc = getDescriptionFromCategory(category);

  return (
    <div className="container-geral-bracket">
      <div className="header-bracket">
        <img src={logo} alt="" style={{ height: "30px" }} />

        <div className="lineHorizontalCategoryTop"></div>
        <h4>{tournamentName}</h4>
        <div className="lineHorizontalCategoryTop"></div>

        <h5>
          {desc.isManual ? (
            <>LUTA CASADA{desc.manualNumber ? ` - ${desc.manualNumber}` : ""}</>
          ) : (
            <>
              {category}, {desc.ageDescription}, {desc.pesoDescription}
              {desc.fightTime ? <> , tempo de luta: {desc.fightTime} min</> : null}
            </>
          )}
        </h5>

        {is32Bracket && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
            (Parte {isPartTwo ? "2" : "1"})
          </Typography>
        )}
      </div>

      <div className="bracket-wrapper">
        <div className="bracket-column">{renderLeftCompetitors()}</div>

        <div className="bracket-center">
          <div className="bracket-line vertical" />
          <div className="final-match">🏆 Final</div>
        </div>

        <div className="bracket-column">{renderRightCompetitors()}</div>
      </div>

      <div>
        <div className="lineHorizontalFooter"></div>
        <div className="footer-bracket">
          <h5>
            {tournamentLocation} , {formatBrazilianDate(tournamentDate)}
          </h5>
          <h5>
            www.torneiofacil.com.br {index + 1}/{totalPages}
          </h5>
        </div>
      </div>
    </div>
  );
}; // ✅ FECHA o TournamentBracket aqui



// Main TournamentBracketsPage component
export default function TournamentBracketsPage() {
  const { tournamentSlug } = useParams<{ tournamentSlug: string }>();

  const [bracketsData, setBracketsData] = useState<any>({});
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [loadingTournamentDetails, setLoadingTournamentDetails] =
    useState(true); // New loading state
  const [loadingBracketData, setLoadingBracketData] = useState(false); // New loading state
  const { getToken, account } = useAuthenticate();

  const [tournamentId, setTournamentId] = useState<string | null>(null);
  const [tournamentName, setTournamentName] = useState<string>("");
  const [tournamentLocation, setTournamentLocation] = useState<string>("");
  const [tournamentDate, setTournamentDate] = useState<string>("");
  const [chaves, setChaves] = useState<any>({});
  const [tournamentRules, setTournamentRules] = useState<any>([])

  const bracketRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const fullScreenContainerRef = useRef<HTMLDivElement>(null); // Ref para o container de escala

  const [fighterNameFilter, setFighterNameFilter] = useState("");
  const [gymFilter, setGymFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  useEffect(() => {
    if (tournamentSlug) {
      setLoadingTournamentDetails(true); // Start loading
      api
        .get(`tournaments/${tournamentSlug}`)
        .then((res) => {
          const tournamentDetails = res.data;
          setTournamentId(tournamentDetails.id);
          setTournamentName(tournamentDetails.name || "");
          setTournamentLocation(tournamentDetails.location || "");
          setTournamentDate(tournamentDetails.date || "");
          setChaves(tournamentDetails.chaves || {});
          setTournamentRules(tournamentDetails.rules)
        })
        .catch((error) => {
          console.error("Error fetching tournament details by slug:", error);
          toast.error("Erro ao carregar detalhes do torneio.");
          setTournamentId(null);
        })
        .finally(() => {
          setLoadingTournamentDetails(false); // End loading
        });
    }
  }, [tournamentSlug]);

  useEffect(() => {
    if (tournamentId) {
      setLoadingBracketData(true); // Start loading
      api
        .get(`administrators/tournaments/${tournamentId}/bracket`)
        .then((res) => {
          setBracketsData(res.data.data || {});
        })
        .catch((error) => {
          console.error("Error fetching bracket data:", error);
          toast.error("Erro ao carregar as chaves do torneio.");
        })
        .finally(() => {
          setLoadingBracketData(false); // End loading
        });
    }
  }, [tournamentId]);

  // **NOVO useEffect para a lógica de escala**
  useEffect(() => {
    const applyScale = () => {
      if (fullScreenContainerRef.current) {
        // Base de largura de referência para a escala (por exemplo, 1200px)
        const referenceWidth = 1200;
        const currentWidth = window.innerWidth;
        const scale = currentWidth / referenceWidth;

        fullScreenContainerRef.current.style.transform = `scale(${scale})`;
        fullScreenContainerRef.current.style.transformOrigin = "top left";
        // Ajusta a largura para que o conteúdo original não fique cortado
        fullScreenContainerRef.current.style.width = `${100 / scale}%`;
        fullScreenContainerRef.current.style.height = `${100 / scale}%`; // Adiciona altura para evitar rolagem horizontal
        fullScreenContainerRef.current.style.overflow = "auto"; // Garante que a rolagem funcione se o conteúdo for maior
      }
    };

    // Aplica a escala na montagem e em cada redimensionamento da janela
    applyScale();
    window.addEventListener("resize", applyScale);

    // Limpa o event listener quando o componente é desmontado
    return () => {
      window.removeEventListener("resize", applyScale);
    };
  }, []); // Array de dependências vazio para rodar apenas uma vez na montagem

  const generateKeys = () => {
    if (!tournamentId) {
      toast.error("ID do torneio não encontrado.");
      return;
    }
    // No need for a separate loading state here since this is triggered by a button click
    // and can use the isGeneratingPdf state if a PDF generation is also initiated.
    // Or, if this generates the brackets internally, a new loading state for generation could be added.
    api
      .post(`administrators/tournaments/${tournamentId}/bracket`, chaves, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      })
      .then((res) => {
        setBracketsData(res.data.data || {});
        toast.success("Chaves geradas com sucesso!");
      })
      .catch((error) => {
        toast.error("Erro ao gerar chaves.");
        console.error("Erro ao gerar chaves:", error);
      });
  };

  const generatePdf = async () => {
    setIsGeneratingPdf(true);
    const toastId = toast.loading("Gerando PDF das chaves...", {
      progress: 0,
    });

    const doc = new jsPDF("l", "mm", "a4");
    let firstPage = true;

    // Use the processed brackets data to iterate through for PDF generation
    const processedBracketsForPdf = Object.entries(filteredAndSplitData);
    const totalBrackets = processedBracketsForPdf?.length;
    let processedCount = 0;
    const startTime = Date.now();

    const formatTime = (ms: number) => {
      const seconds = Math.floor(ms / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return minutes > 0
        ? `${minutes} min ${remainingSeconds} seg`
        : `${remainingSeconds} seg`;
    };

    for (const [key, data] of processedBracketsForPdf) {
      const input = bracketRefs.current[key]; // Use the key directly
      if (input) {
        if (!firstPage) {
          doc.addPage();
        } else {
          firstPage = false;
        }

        try {
          const canvas = await html2canvas(input, { scale: 2 });
          const imgData = canvas.toDataURL("image/png", 1);

          const imgWidth = 297;
          const pageHeight = 210;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let heightLeft = imgHeight;
          let position = 0;

          doc.addImage(imgData, "png", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            doc.addPage();
            doc.addImage(imgData, "png", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
        } catch (error) {
          console.error("Erro ao gerar canvas para", key, error);
          toast.update(toastId, {
            render: `Erro ao gerar PDF para ${key}.`,
            type: "error",
            isLoading: false,
            autoClose: 5000,
            theme: "colored",
          });
          setIsGeneratingPdf(false);
          return;
        }
      }
      processedCount++;
      const elapsedTime = Date.now() - startTime;
      const avgTimePerBracket = elapsedTime / processedCount;
      const remainingBrackets = totalBrackets - processedCount;
      const estimatedRemainingTime = avgTimePerBracket * remainingBrackets;

      toast.update(toastId, {
        render: `Gerando PDF: ${processedCount}/${totalBrackets} chaves (${formatTime(
          estimatedRemainingTime
        )} restantes)...`,
        progress: processedCount / totalBrackets,
      });
    }

    doc.save(`${tournamentName}_chaves.pdf`);
    setIsGeneratingPdf(false);
    toast.update(toastId, {
      render: "PDF das chaves gerado com sucesso!",
      type: "success",
      autoClose: 5000,
      isLoading: false,
      theme: "colored",
    });
  };

  const filterAndSplitBracketsData = () => {
    let currentFilteredData: any = {};

    Object.entries(bracketsData).forEach(([key, data]: any) => {
      const hasMatchingFighter = data.matchStructure.some((match: any[]) =>
        match.some((m: any) => {
          if (!m.lutador || typeof m.lutador !== "object") return false;
          const name = m.lutador.name?.toLowerCase() || "";
          const gym = m.lutador.gym?.toLowerCase() || "";

          const nameMatches =
            !fighterNameFilter ||
            name.includes(fighterNameFilter.toLowerCase());
          const gymMatches =
            !gymFilter || gym.includes(gymFilter.toLowerCase());

          return nameMatches && gymMatches;
        })
      );

      const categoryMatches =
        !categoryFilter ||
        key.toLowerCase().includes(categoryFilter.toLowerCase());

      if (hasMatchingFighter && categoryMatches) {
        const competitorCount = data.matchStructure
          .flat()
          .filter(
            (c: any) => c.lutador && typeof c.lutador === "object"
          ).length;

        if (competitorCount > 16) {
          // Split into two parts for 32-person bracket (or more)
          currentFilteredData[`${key}-Parte1`] = {
            ...data,
            is32Bracket: true,
            isPartTwo: false,
            competitorsForPart: data.matchStructure, // Still pass all competitors, logic handles rendering
          };
          currentFilteredData[`${key}-Parte2`] = {
            ...data,
            is32Bracket: true,
            isPartTwo: true,
            competitorsForPart: data.matchStructure,
          };
        } else {
          currentFilteredData[key] = {
            ...data,
            is32Bracket: false,
            isPartTwo: false,
            competitorsForPart: data.matchStructure,
          };
        }
      }
    });
    return currentFilteredData;
  };

  const filteredAndSplitData = filterAndSplitBracketsData();
  const availableCategories = Object.keys(bracketsData || {}).sort();

  return (
    <>
      <NavBar />
      <Box sx={{ backgroundColor: "#FFF" }}>
        <Typography id="page-title" variant="h6" component="h1" gutterBottom>
          Chaves do Torneio: <strong>{tournamentName}</strong>
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            label="Nome do Lutador"
            sx={{ flex: "1 1 300px" }}
            value={fighterNameFilter}
            onChange={(e) => setFighterNameFilter(e.target.value)}
          />
          <TextField
            label="Academia"
            sx={{ flex: "1 1 300px" }}
            value={gymFilter}
            onChange={(e) => setGymFilter(e.target.value)}
          />
          <Autocomplete
            sx={{ flex: "1 1 300px" }}
            options={availableCategories}
            value={categoryFilter}
            onChange={(event, newValue: string | null) => {
              setCategoryFilter(newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Categoria" />
            )}
            clearOnEscape
          />
        </Box>
      </Box>

      <div className="full-screen-container" ref={fullScreenContainerRef}>
        <Box sx={pageContainerStyle}>
          <Box sx={{ overflowY: "auto", flexGrow: 1, p: 1 }}>
            {loadingTournamentDetails || loadingBracketData ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>
                  Carregando chaves do torneio...
                </Typography>
              </Box>
            ) : tournamentId === null && !tournamentSlug ? (
              <Typography>Nenhum slug de torneio fornecido na URL.</Typography>
            ) : tournamentId === null ? (
              <Typography>Carregando detalhes do torneio...</Typography>
            ) : Object.keys(bracketsData || {}).length > 0 ? (
              <>
                {Object.keys(filteredAndSplitData).length > 0 ? (
                  Object.keys(filteredAndSplitData).map((item: any, index) => {
                    const bracketInfo = filteredAndSplitData[item];
                    return (
                      <div
                        key={item}
                        ref={(el) => (bracketRefs.current[item] = el)}
                        style={{ marginBottom: "20px" }}
                      >
                        <TournamentBracket
                          competitors={bracketInfo?.competitorsForPart || []}
                          category={item
                            .replace("-Parte1", "")
                            .replace("-Parte2", "")}
                          tournamentName={tournamentName}
                          tournamentDate={tournamentDate}
                          tournamentLocation={tournamentLocation}
                          index={index}
                          totalPages={Object.keys(filteredAndSplitData).length}
                          fighterNameFilter={fighterNameFilter}
                          gymFilter={gymFilter}
                          is32Bracket={bracketInfo.is32Bracket}
                          isPartTwo={bracketInfo.isPartTwo}
                          rules={tournamentRules}
                        />
                      </div>
                    );
                  })
                ) : (
                  <Typography sx={{ mt: 2 }}>
                    Nenhuma chave encontrada com os filtros aplicados.
                  </Typography>
                )}
              </>
            ) : (
              // <Typography sx={{ mt: 2 }}>
              //   Ainda não foram geradas chaves para este torneio.
              // </Typography>
              <BlinkText style={{ marginTop: 28 }}>
                <b>Ainda não foram geradas chaves para este torneio.</b>
                <br></br>
                <b>AGUARDE ...</b>
              </BlinkText>
            )}
          </Box>

          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            {account.id == 34 &&
              Object.keys(filteredAndSplitData).length > 0 && (
                <Button
                  variant="contained"
                  onClick={generatePdf}
                  disabled={isGeneratingPdf}
                >
                  {isGeneratingPdf ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Gerar PDF das Chaves"
                  )}
                </Button>
              )}
          </Box>
          <ToastContainer position="top-center" />
        </Box>
      </div>
    </>
  );
}
